import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback } from "react";
import { Box, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { CalendarDays } from "lucide-react";
import { setAffiliateSortConfig } from "../../../redux/monthlyProjectionSlice";
import CommonTable from "../../../common_components/CommonTable";
import CommonLoader from "../../../common_components/CommonLoader";
import InfoTooltip from "../../../common_components/InfoTooltip";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import { StyledCurrency } from "../../../helper/StyledCurrency";
import { EXPAND_TRIGGERS } from "../../../common_components/tableConstants";
import { NETWORK_ICON_MAP, NETWORK_LABEL_MAP } from "../../../utils/networkConstants";
const formatCurrency = (value, hideSymbol) => {
    const num = Number(value) || 0;
    const formatted = num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return hideSymbol ? formatted : `$${formatted}`;
};
const ymdLabel = (ym) => moment(ym, "YYYY-MM").format("MMM YYYY").toUpperCase();
// Shown for any null value (data not available yet).
const NAText = () => (_jsx("span", { style: { fontStyle: "italic", color: "#98A2B3", fontSize: 13 }, children: "N/A" }));
// Site channel label -> icon (site is always Shopify or Google Analytics).
const SITE_CHANNEL_ICON = {
    "Shopify": "/assets/shopify.svg",
    "Google Analytics": "/assets/analytics.svg",
};
// Initials from a name: first letter of first word + first letter of last word.
const nameInitials = (name) => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length)
        return "";
    const first = parts[0][0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase();
};
// Avatar colours, picked deterministically per name so each AM keeps a consistent colour.
const AM_AVATAR_COLORS = [
    { bg: "#FCE7F3", fg: "#BE185D" },
    { bg: "#FEF3C7", fg: "#B45309" },
    { bg: "#DBEAFE", fg: "#1D4ED8" },
    { bg: "#DCFCE7", fg: "#15803D" },
    { bg: "#EDE9FE", fg: "#6D28D9" },
];
const amColorFor = (name) => {
    const sum = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
    return AM_AVATAR_COLORS[sum % AM_AVATAR_COLORS.length];
};
// Client name + tier (i) tooltip + (!)/(*) marker (matches the old-portal store-name column).
const ClientNameCell = ({ name, marker, note, siteNote }) => (_jsxs(Box, { sx: { display: "inline-flex", alignItems: "center", gap: 0.5 }, children: [_jsx("span", { style: { fontSize: 14, color: "#101828" }, children: name || "—" }), note ? _jsx(InfoTooltip, { title: note, placement: "top", size: 14 }) : null, marker ? _jsx("span", { style: { fontSize: 12, color: "#98A2B3" }, children: marker }) : null, siteNote ? (_jsx(HoverTooltip, { title: siteNote, placement: "top", children: _jsx(Box, { sx: { display: "flex", alignItems: "center", color: "#667085", cursor: "pointer", ml: 0.5 }, children: _jsx(CalendarDays, { size: 14 }) }) })) : null] }));
// Currency — italic N/A when null, dark when there's a value, gray when $0.
const CurrencyCell = ({ value, hideSymbol }) => {
    if (value === null || value === undefined)
        return _jsx(NAText, {});
    return (_jsx(StyledCurrency, { value: formatCurrency(value, hideSymbol), fontSize: "14px", valueWeight: 400, valueColor: value ? "#101828" : "#98A2B3" }));
};
// Plain number / count / conv-rate — gray zero, optional suffix (e.g. "%").
const NumCell = ({ value, suffix = "", decimals = 0 }) => {
    const num = Number(value) || 0;
    const text = num.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    const valueColor = num ? "#101828" : "#98A2B3";
    if (suffix === "%") {
        return (_jsx(StyledCurrency, { value: `${text}%`, fontSize: "14px", valueWeight: 400, symbolWeight: 400, valueColor: valueColor, symbolColor: "#98A2B3" }));
    }
    return (_jsxs("span", { style: { fontSize: 14, fontWeight: 400, color: valueColor }, children: [text, suffix] }));
};
const YoyPill = ({ value, baseMonth, tierNote }) => {
    if (value === null || value === undefined) {
        let extractedDate = "";
        if (tierNote) {
            const multiMatch = tierNote.match(/months \((.*?)\)/);
            const singleMatch = tierNote.match(/based on (.*?) data/);
            if (multiMatch && multiMatch[1])
                extractedDate = `(${multiMatch[1]})`;
            else if (singleMatch && singleMatch[1])
                extractedDate = singleMatch[1];
        }
        const text = extractedDate ? `Data not found, using ${extractedDate} data.` : `Data not found. Projection is based on recent averages.`;
        return (_jsx(HoverTooltip, { title: text, placement: "top", children: _jsx(Box, { sx: { display: "inline-flex", alignItems: "center", bgcolor: "#F2F4F7", borderRadius: "16px", px: 1, py: 0.2, cursor: "help" }, children: _jsx(StyledCurrency, { value: "0%", fontSize: "13px", valueColor: "#667085", symbolColor: "#667085" }) }) }));
    }
    const v = Number(value);
    const isPos = v >= 0;
    return (_jsxs(Box, { sx: { display: "inline-flex", alignItems: "center", gap: 0.5, bgcolor: isPos ? "#ecfdf3" : "#fef3f2", borderRadius: "16px", px: 1, py: 0.2 }, children: [_jsx(StyledCurrency, { value: `${Math.abs(v).toFixed(2)}%`, fontSize: "13px", valueWeight: 500, symbolWeight: 500, valueColor: isPos ? "#027a48" : "#b42318", symbolColor: isPos ? "#027a48" : "#b42318" }), _jsx("img", { src: isPos ? "/assets/trend-up.svg" : "/assets/trending-down.svg", alt: "", style: { width: 12, height: 12 } })] }));
};
// ROAS — italic N/A when null, plain ratio otherwise.
const RoasCell = ({ value }) => {
    if (value === null || value === undefined)
        return _jsx(NAText, {});
    const num = Number(value) || 0;
    return (_jsx("span", { style: { fontSize: 14, fontWeight: 400, color: num ? "#101828" : "#98A2B3" }, children: num ? num.toFixed(2) : "0" }));
};
// Plain text — gray "—" when empty.
const TextCell = ({ value }) => (_jsx("span", { style: { fontSize: 14, fontWeight: 400, color: value ? "#101828" : "#98A2B3" }, children: value || "—" }));
// Site measurement channel — icon (Shopify / Google Analytics), italic N/A when empty.
const SiteChannelCell = ({ value }) => {
    if (!value)
        return _jsx(NAText, {});
    const icon = SITE_CHANNEL_ICON[value];
    return icon ? (_jsx(Tooltip, { title: value, arrow: true, children: _jsx("img", { src: icon, alt: value, style: { width: 22, height: 22, borderRadius: 4, objectFit: "contain" } }) })) : (_jsx("span", { style: { fontSize: 13, color: "#101828" }, children: value }));
};
// Account manager(s) — initials avatars (one per AM), italic N/A when none.
const AmAvatarsCell = ({ value }) => {
    const names = (value || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (!names.length)
        return _jsx(NAText, {});
    return (_jsx(Box, { sx: { display: "inline-flex", alignItems: "center" }, children: names.map((name, idx) => {
            const c = amColorFor(name);
            return (_jsx(HoverTooltip, { title: name, children: _jsx(Box, { sx: {
                        width: 28, height: 28, borderRadius: "50%",
                        bgcolor: c.bg, color: c.fg,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 11, fontWeight: 600, border: "2px solid #fff",
                        marginLeft: idx === 0 ? 0 : "-8px",
                    }, children: nameInitials(name) }) }, idx));
        }) }));
};
// Connected measurement channels — icons where available, text chip otherwise.
const ChannelIconsCell = ({ networks }) => {
    const displayNetworks = (networks || []).filter(n => {
        const k = n.toLowerCase().replace(/[^a-z]/g, '');
        return k !== 'shareasale';
    });
    if (displayNetworks.length === 0) {
        return _jsx("span", { style: { fontSize: 14, color: "#98A2B3" }, children: "\u2014" });
    }
    return (_jsx(Box, { sx: { display: "inline-flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }, children: displayNetworks.map((n) => {
            const key = n.toLowerCase();
            const label = NETWORK_LABEL_MAP[key] || n;
            const icon = NETWORK_ICON_MAP[key];
            return icon ? (_jsx(Tooltip, { title: label, arrow: true, children: _jsx("img", { src: icon, alt: label, style: { width: 20, height: 20, borderRadius: 4, objectFit: "contain" } }) }, n)) : (_jsx(Box, { sx: { fontSize: 11, fontWeight: 500, color: "#344054", bgcolor: "#F2F4F7", borderRadius: "6px", px: 0.75, py: 0.2 }, children: label }, n));
        }) }));
};
// Notes chip — highlights "Not connected" / "No data found".
const NotesCell = ({ value }) => {
    if (!value)
        return _jsx("span", { style: { fontSize: 14, color: "#98A2B3" }, children: "\u2014" });
    const isNotConnected = /not connected/i.test(value);
    return (_jsx(Box, { sx: {
            display: "inline-flex", alignItems: "center", borderRadius: "16px", px: 1, py: 0.2, fontSize: 12, fontWeight: 500,
            bgcolor: isNotConnected ? "#FEF3F2" : "#FFFAEB",
            color: isNotConnected ? "#B42318" : "#B54708",
        }, children: value }));
};
const getColumns = (baseLabel, targetLabel) => [
    { id: "client_name", label: "Client Name", minWidth: 200, align: "left", sortable: true },
    { id: "aff_outreach", label: "Outreach", minWidth: 110, align: "left", sortable: true },
    { id: "aff_intro_am", label: "Intro To AM", minWidth: 120, align: "left", sortable: true },
    { id: "aff_conv_rate", label: "Conv. Rate", minWidth: 110, align: "left", sortable: true },
    { id: "aff_conversations", label: "Conversations", minWidth: 130, align: "left", sortable: true },
    { id: "aff_published", label: "Published", minWidth: 110, align: "left", sortable: true },
    { id: "site_base_revenue", label: `Site ${baseLabel} Rev`, minWidth: 170, align: "left", sortable: true },
    { id: "site_yoy_growth_rate", label: "Site YoY Growth Rate", minWidth: 180, align: "left", sortable: true },
    { id: "site_measurement_channel", label: "Site Measurement Channel", minWidth: 200, align: "left", sortable: true },
    { id: "site_projected_revenue", label: `Site Proj ${targetLabel} Rev`, minWidth: 190, align: "left", sortable: true },
    { id: "mc_base_revenue", label: `${baseLabel} Rev`, minWidth: 160, align: "left", sortable: true },
    { id: "mc_yoy_growth_rate", label: "YoY Growth Rate", minWidth: 170, align: "left", sortable: true },
    { id: "measurement_channels", label: "Measurement Channel", minWidth: 190, align: "left" },
    { id: "mc_projected_revenue", label: `Proj ${targetLabel} Rev`, minWidth: 180, align: "left", sortable: true },
    { id: "projected_roas", label: "Proj ROAS", minWidth: 120, align: "left", sortable: true },
    { id: "am_projection", label: "AM Projection", minWidth: 160, align: "left", sortable: true },
    { id: "actual_revenue", label: "Actual Revenue", minWidth: 160, align: "left", sortable: true },
    { id: "actual_spend", label: "Actual Spend", minWidth: 150, align: "left", sortable: true },
    { id: "actual_roas", label: "Actual ROAS", minWidth: 130, align: "left", sortable: true },
    { id: "account_manager", label: "Account Manager", minWidth: 190, align: "left", sortable: true },
    { id: "account_type", label: "Type", minWidth: 120, align: "left", sortable: true },
    { id: "aff_site_goal", label: "Site Goal", minWidth: 130, align: "left", sortable: true },
    { id: "clockify_goal", label: "Clockify Goal", minWidth: 130, align: "left", sortable: true },
    { id: "notes", label: "Notes", minWidth: 160, align: "left", sortable: true },
];
const ALL_COLUMN_IDS = [
    "client_name", "aff_outreach", "aff_intro_am", "aff_conv_rate", "aff_conversations", "aff_published",
    "site_base_revenue", "site_yoy_growth_rate", "site_projected_revenue", "site_measurement_channel",
    "mc_base_revenue", "mc_yoy_growth_rate", "mc_projected_revenue", "projected_roas", "am_projection",
    "actual_revenue", "actual_spend", "actual_roas", "measurement_channels", "account_manager", "account_type", "aff_site_goal", "clockify_goal", "notes",
];
/**
 * Affiliate Projection tab body. Two revenue blocks per client (site-wide + measurement-channel),
 * a spend-derived Proj ROAS, target-month actuals, connected-channel icons, AM, type and notes.
 * Controls (target month, Generate, Export, search, view-columns) live in the shared header.
 */
export default function AffiliateProjection({ hiddenColumns, onColumnVisibilityChange, columnVisibilityAnchor = null, onColumnVisibilityClose, pinnedColumns, onColumnPinningChange, }) {
    const dispatch = useDispatch();
    const { affiliateRows, affiliateMeta, affiliateLoading, affiliateGenerating, month, affiliateSortBy, affiliateSortOrder, } = useSelector((state) => state.monthlyProjection);
    const baseLabel = affiliateMeta?.base_year_month_label || ymdLabel(moment(month, "YYYY-MM").subtract(1, "year").format("YYYY-MM"));
    const targetLabel = affiliateMeta?.year_month_label || ymdLabel(month);
    const renderCell = useCallback((row, col) => {
        const hideSymbol = /order/i.test(row.account_type || "");
        switch (col.id) {
            case "client_name":
                return _jsx(ClientNameCell, { name: row.client_name, marker: row.tier_marker, note: row.tier_note, siteNote: row.site_tier_note });
            case "aff_outreach":
                return _jsx(NumCell, { value: row.aff_outreach });
            case "aff_intro_am":
                return _jsx(NumCell, { value: row.aff_intro_am });
            case "aff_conv_rate":
                return _jsx(NumCell, { value: row.aff_conv_rate, suffix: "%" });
            case "aff_conversations":
                return _jsx(NumCell, { value: row.aff_conversations });
            case "aff_published":
                return _jsx(NumCell, { value: row.aff_published });
            case "site_base_revenue":
                return _jsx(CurrencyCell, { value: row.site_base_revenue, hideSymbol: hideSymbol });
            case "site_yoy_growth_rate":
                return _jsx(YoyPill, { value: row.site_yoy_growth_rate, baseMonth: baseLabel, tierNote: row.site_tier_note });
            case "site_projected_revenue":
                return _jsx(CurrencyCell, { value: row.site_projected_revenue, hideSymbol: hideSymbol });
            case "site_measurement_channel":
                return _jsx(SiteChannelCell, { value: row.site_measurement_channel });
            case "mc_base_revenue":
                return _jsx(CurrencyCell, { value: row.mc_base_revenue, hideSymbol: hideSymbol });
            case "mc_yoy_growth_rate":
                return _jsx(YoyPill, { value: row.mc_yoy_growth_rate, baseMonth: baseLabel, tierNote: row.tier_note });
            case "mc_projected_revenue":
                return _jsx(CurrencyCell, { value: row.mc_projected_revenue, hideSymbol: hideSymbol });
            case "projected_roas":
                return _jsx(RoasCell, { value: row.projected_roas });
            case "am_projection":
                return _jsx(CurrencyCell, { value: row.am_projection, hideSymbol: hideSymbol });
            case "actual_revenue":
                return _jsx(CurrencyCell, { value: row.actual_revenue, hideSymbol: hideSymbol });
            case "actual_spend":
                return _jsx(CurrencyCell, { value: row.actual_spend });
            case "actual_roas":
                return _jsx(RoasCell, { value: row.actual_roas });
            case "measurement_channels":
                return _jsx(ChannelIconsCell, { networks: row.measurement_channels });
            case "account_manager":
                return _jsx(AmAvatarsCell, { value: row.account_manager });
            case "account_type":
                return _jsx(TextCell, { value: row.account_type });
            case "aff_site_goal":
                return _jsx(CurrencyCell, { value: row.aff_site_goal });
            case "clockify_goal":
                return _jsx(NumCell, { value: row.clockify_goal });
            case "notes":
                return _jsx(NotesCell, { value: row.notes });
            default:
                return "";
        }
    }, []);
    return (_jsxs(Box, { className: "table-container", sx: { position: "relative", background: "#fff", borderRadius: "8px", border: "1px solid #D0D5DD" }, children: [_jsx(CommonTable, { columns: getColumns(baseLabel, targetLabel), rows: affiliateRows, renderCell: renderCell, isFirstLoad: affiliateLoading && !affiliateRows.length, sortConfig: { columnId: affiliateSortBy, direction: affiliateSortOrder }, onSortChange: (id, dir) => dispatch(setAffiliateSortConfig({ sortBy: id, sortOrder: dir || "asc" })), expandTrigger: EXPAND_TRIGGERS.NONE, enableColumnActions: true, enableColumnReorder: false, enableColumnPinning: true, pinnedColumns: pinnedColumns, onColumnPinningChange: onColumnPinningChange, enableColumnFilters: false, enableSelection: false, columnVisibilityConfig: { enable: true, columns: ALL_COLUMN_IDS }, hiddenColumns: hiddenColumns, onColumnVisibilityChange: onColumnVisibilityChange, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: onColumnVisibilityClose, isLoading: false, stickyHeader: true, maxHeight: "calc(100vh - 300px)", tableHeadClassName: "reusable-table-header-label all-Font-Sizes" }), (affiliateLoading || affiliateGenerating) && (_jsx(Box, { sx: { position: "absolute", inset: 0, background: "white", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }, children: _jsx(CommonLoader, { className: "table-loader" }) })), affiliateRows.length > 0 && (_jsxs(Box, { className: "mp-table-footer", children: ["Showing 1 to ", affiliateRows.length, " of ", affiliateRows.length, " entries"] }))] }));
}
